import { environment } from 'src/environments/environment';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Domain } from 'src/app/services/domain.service';
import { User } from 'src/app/services/auth.service';
import { selectDomain, selectUser } from 'src/app/store/selectors/auth.selectors';
import { AlertController, Platform } from '@ionic/angular';
import { logout, setDomain } from 'src/app/store/actions/auth.actions';
import { trigger, style, transition, animate } from '@angular/animations';
import { ApiConfig, ConfigService } from 'src/app/services/config.service';
import { StatusBarService } from 'src/app/services/status-bar.service';
import { Style } from '@capacitor/status-bar';
import { AiOverlayService } from 'src/app/services/ai-overlay.service';
import { sortDomainsByLabel } from 'src/app/utils/domain-sort';
import { filterSelectableDomains } from 'src/app/utils/domain-visibility';
@Component({
  selector: 'mvp-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('0.5s ease-in', style({ transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        animate('0.5s ease-out', style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {

  config: ApiConfig;
  user: User;
  domain: Domain;
  domain$: Observable<Domain>;
  assetUrl: string = environment.assetUrl;
  aiFirstMessage?: string;
  aiOnboarding = false;
  currentRoute: string;
  isMenuOpen = false;
  isAiOverlayOpen = false;
  isDomainSelectorOpen = false;
  userDomains: Domain[] = [];
  private backButtonSubscription?: Subscription;
  private aiOverlaySubscription?: Subscription;

  constructor(
    private store: Store,
    private router: Router,
    private alertController: AlertController,
    private configService: ConfigService,
    private platform: Platform,
    private statusBarService: StatusBarService,
    private aiOverlayService: AiOverlayService
  ) {
    this.config = configService.apiConfig;
    this.aiOverlaySubscription = this.aiOverlayService.overlay$.subscribe(({ isOpen, firstMessage, onboarding }) => {
      this.aiFirstMessage = firstMessage;
      this.aiOnboarding = !!onboarding;

      if (isOpen) {
        this.openAiOverlay();
      } else {
        this.isAiOverlayOpen = false;
        this.unregisterAiBackButton();
      }
    });
    this.store.select(selectUser).pipe(
      filter((user: User) => user !== null)
    ).subscribe((user: User) => {
      this.user = user;
      this.userDomains = sortDomainsByLabel(filterSelectableDomains(user?.domains || []));
    });
    this.domain$ = this.store.select(selectDomain);
    this.store.select(selectDomain).pipe(
      filter((domain: Domain) => domain !== null)
    ).subscribe((domain: Domain) => {
      this.domain = domain;
    });
    // set currentRoute
    this.currentRoute = this.router.url;
    // events on Router Update
    router.events.subscribe((link) => {
      // get current url path
      if (link instanceof NavigationEnd) {
        this.currentRoute = link.url;
        // Reset menu state on every route change
        this.isMenuOpen = false;
        this.isDomainSelectorOpen = false;
      }
    });
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.unregisterAiBackButton();
    this.aiOverlaySubscription?.unsubscribe();
  }

  getTitleColor(): string {
    const bg = this.domain?.color || this.config?.app?.ui_color_bg || '#000000';
    const statusBarStyle = this.statusBarService.getStyleForBackground(bg);
    // Match status bar icon color
    return statusBarStyle === Style.Light ? '#000000' : '#ffffff';
  }

  toggleDomainSelector() {
    this.isDomainSelectorOpen = !this.isDomainSelectorOpen;
  }

  switchDomain(domain: Domain) {
    if (domain.uuid === this.domain.uuid) {
      this.toggleDomainSelector();
      return;
    }

    this.store.dispatch(setDomain({ domain }));

    this.toggleDomainSelector();
    this.closeMenu();

    window.location.reload();
  }

  getAvatarUrl() {
    if (!this.user || !this.user.avatar) {
      return '';
    }

    return `${this.assetUrl}/avatars/${this.user.avatar}.png`;
  }
  getLogoUrl() {
    return `${this.assetUrl}/logos/`;
  }

  openMenu() {
    this.isMenuOpen = true;
  }

  closeMenu() {
    this.isMenuOpen = false;
  }

  openAiOverlay() {
    this.isMenuOpen = false;
    this.isDomainSelectorOpen = false;
    this.isAiOverlayOpen = true;
    this.registerAiBackButton();
  }

  closeAiOverlay() {
    this.aiOverlayService.close();
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  openExternalWebsite(url: string) {
    window.open(url, '_blank');
  }

  isIOS() {
    return this.platform.is('ios');
  }

  isAndroid() {
    return this.platform.is('android');
  }

  async logout() {
    const alert = await this.alertController.create({
      header: 'Déconnexion',
      message: 'Souhaitez-vous vous déconnecter ?',
      buttons: [
        {
          text: 'Annuler',
          role: 'cancel',
          cssClass: 'secondary',
        }, {
          text: 'Déconnexion',
          handler: () => {
            this.store.dispatch(logout());
          }
        }
      ]
    });
    await alert.present();
  }

  handleProfileClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isMenuOpen = false;
    this.isDomainSelectorOpen = false;
    setTimeout(() => {
      this.router.navigate(['/profile']);
    }, 50);
  }

  private registerAiBackButton() {
    if (this.backButtonSubscription) {
      return;
    }

    this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(10000, () => {
      if (this.isAiOverlayOpen) {
        this.closeAiOverlay();
      }
    });
  }

  private unregisterAiBackButton() {
    if (!this.backButtonSubscription) {
      return;
    }

    this.backButtonSubscription.unsubscribe();
    this.backButtonSubscription = undefined;
  }

}
